import axiosInstance, { TokenManager } from "./utils/axiosConfig";
import { useEffect, useState, useCallback, useMemo } from "react";
import Routes from "./Routes";
import Snackbar from "@material-ui/core/Snackbar";
import { CircularProgress } from '@material-ui/core';
import MuiAlert from "@material-ui/lab/Alert";
import { PusherProvider } from "./Pusher/PusherProvider";
import { PusherManager } from "./Pusher/PusherManager";
import { refreshToken } from "./authUtils";
import "./App.css";


// Session persistence hook to prevent logout due to inactivity
const useSessionPersistence = (user) => {
    useEffect(() => {
        if (!user) return;
        
        // Refresh token every 45 minutes (JWT tokens typically expire in 60 minutes)
        const refreshInterval = setInterval(async () => {
            console.log('Session persistence: Refreshing token...');
            try {
                const newToken = await refreshToken();
                if (newToken) {
                    console.log('Session persistence: Token refreshed successfully');
                } else {
                    console.warn('Session persistence: Failed to refresh token');
                    // Don't redirect here - let the axios interceptor handle it
                }
            } catch (error) {
                console.error('Session persistence: Error refreshing token:', error);
                // Don't redirect here - let the axios interceptor handle it
            }
        }, 45 * 60 * 1000); // 45 minutes

        // Also refresh on user activity to ensure active users stay logged in
        let activityTimer;
        const resetActivityTimer = () => {
            clearTimeout(activityTimer);
            activityTimer = setTimeout(async () => {
                console.log('Session persistence: User activity detected, refreshing token...');
                try {
                    const newToken = await refreshToken();
                    if (newToken) {
                        console.log('Session persistence: Token refreshed due to activity');
                    }
                } catch (error) {
                    console.error('Session persistence: Activity-based refresh failed:', error);
                }
            }, 30 * 60 * 1000); // Refresh after 30 minutes of activity
        };

        // Listen for user activity
        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        activityEvents.forEach(event => {
            document.addEventListener(event, resetActivityTimer, true);
        });

        // Initial activity timer
        resetActivityTimer();

        // Cleanup
        return () => {
            clearInterval(refreshInterval);
            clearTimeout(activityTimer);
            activityEvents.forEach(event => {
                document.removeEventListener(event, resetActivityTimer, true);
            });
        };
    }, [user]);
};

// Main App component
function App() {
    const { user, setUser, isParent, isMentor, kids, selectChild, setSelectChild, isDataFetched } = useAuth();
    console.log("🚀 ~ file: App.jsx ~ App ~ TOP LEVEL PROP OBJECT ~ user:", {user});
    const { open, message, severity, showNotification, hideNotification } = useNotification();
    
    // Add session persistence to prevent logout due to inactivity
    useSessionPersistence(user);
    
    // State for Pusher
    const [isPusherInitialized, setIsPusherInitialized] = useState(false);
    
    const handlePusherConnectionChange = useCallback((status, error) => {
    console.log('PusherConnectionChange:', status, error); // Debug log
    
    if (status === 'error') {
        // Check if user is a mentor and if error can be safely ignored
        const isMentor = user?.roles?.some(role => role.name === "mentor" || role.id === 3);
        
        if (isMentor && (error?.response?.status === 500 || error?.toString().includes('authorization'))) {
            console.log('Suppressing Pusher error for mentor user - continuing without real-time updates');
            setIsPusherInitialized(true); // Allow the app to continue
        } else {
            console.error('Pusher connection error:', error);
            showNotification('Lost connection to real-time updates', 'error');
        }
    } else {
        console.log('Setting isPusherInitialized to true'); // Debug log
        setIsPusherInitialized(true);
        console.log('Successfully connected to Pusher!');
    }
    }, [showNotification]);
    
    // Memoized theme to prevent unnecessary re-renders
    const theme = useMemo(() => {
        return user?.gender === "M"
            ? {
                primaryColor: "#3344cc",
                secondaryColor: "#ababdc",
                barColor: "hsl(29deg 91% 79%)",
                backgroundLogo: "/images/m-racing.jpg"
            }
            : {
                primaryColor: "#eb4c8a",
                secondaryColor: "#fccfdf",
                barColor: "#fcedf3",
                backgroundLogo: "/images/f-meadow.jpg"
            };
    }, [user?.gender]);
    
    // Callback for selecting a child user
    const proceedWithChild = useCallback((selectedUser) => {
        // Clear any existing child-specific data
        setSelectChild(false);
        
        // Store the original parent ID before overriding
        const originalParentId = user.id;
        
        // Update the user state with the selected child data
        // IMPORTANT: Override parent properties with child data so components see child's avatar, name, etc.
        const updatedUserData = { 
            ...user,          // Keep parent user base data
            // Override with child's properties so Dashboard/Profile see child's avatar
            id: selectedUser.id,
            name: selectedUser.name,
            display_name: selectedUser.display_name,
            avatar_id: selectedUser.avatar_id,
            avatar: selectedUser.avatar,  // CRITICAL: Use child's avatar, not parent's
            gender: selectedUser.gender,  // Use child's gender for theming
            nationality: selectedUser.nationality,
            // Store reference to selected child for context
            selectedChild: selectedUser,  // Store the full selected child
            childId: selectedUser.id,     // Store child ID for easy reference
            childSettings: selectedUser.settings, // Store child settings
            // CRITICAL: Store original parent ID for API calls that need parent authentication
            parentId: originalParentId,  // Use this when parent_id query param is needed
        };
        
        setUser(updatedUserData);
        
        // CRITICAL FIX: Store the modified user data to localStorage to prevent refresh inconsistencies
        // This ensures components loading from localStorage get the same data as React state
        window.localStorage.setItem("USER", JSON.stringify(updatedUserData));
        
        // Log the change for debugging
        console.log(`Switched to child: ${selectedUser.name} (ID: ${selectedUser.id})`);
        console.log('Updated localStorage with child-modified user data to prevent refresh inconsistencies');
        console.log('proceedWithChild avatar data:', {
            parent_avatar: user?.avatar,
            child_avatar: selectedUser?.avatar,
            updated_user_avatar: updatedUserData?.avatar,
            gender_changed: user?.gender !== selectedUser?.gender
        });
    }, [user, setUser]);
    
    // Only show loading state if we have a user but Pusher isn't initialized yet
    if (user && !isPusherInitialized) {
        console.log('Showing initialization state'); // Debug log
        return (
            <PusherProvider user={user}>
                <PusherManager 
                    user={user} 
                    onConnectionChange={handlePusherConnectionChange} 
                />
                <CircularProgress style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: "#f2722d", width: 50, height: 50 }} />
            </PusherProvider>
        );
    }
    
    return (
        <>
            <PusherProvider user={user}>
                <PusherManager user={user} onConnectionChange={handlePusherConnectionChange} />
                <Routes
                    user={user}
                    isParent={isParent}
                    isMentor={isMentor}
                    kids={kids}
                    setUser={setUser}
                    selectChild={selectChild}
                    proceedWithChild={proceedWithChild}
                    theme={theme}
                    showNotification={showNotification}
                    isDataFetched={isDataFetched}
                />
                <Snackbar open={open} autoHideDuration={6000} onClose={hideNotification}>
                    <MuiAlert elevation={6} variant="filled" onClose={hideNotification} severity={severity}>
                        {message}
                    </MuiAlert>
                </Snackbar>
            </PusherProvider>
        </>
    );
};

// TODO: Move this to a separate file and import it
// Custom hook for managing authentication state
function useAuth() {
    const [user, setUser] = useState(null);
    const [isParent, setIsParent] = useState(false);
    const [isMentor, setIsMentor] = useState(false);
    const [kids, setKids] = useState(null);
    const [selectChild, setSelectChild] = useState(false);
    const [isDataFetched, setIsDataFetched] = useState(false);
    
    const fetchUser = useCallback(async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const urlToken = urlParams.get('token');
            const storedToken = window.localStorage.getItem('JWT_TOKEN');
            const storedUser = window.localStorage.getItem('USER');
            
            // If there's already processed user data in localStorage, use it
            // But check if we should force a fresh fetch (e.g., after profile updates)
            if (storedUser && storedToken) {
                try {
                    const cachedUserData = JSON.parse(storedUser);
                    
                    // Check if we should force a fresh fetch from backend
                    // This happens when the page was reloaded after a profile update
                    const shouldForceFresh = sessionStorage.getItem('FORCE_FRESH_USER_DATA') === 'true';
                    
                    if (shouldForceFresh) {
                        console.log('App.jsx: Force fetching fresh user data due to profile update');
                        sessionStorage.removeItem('FORCE_FRESH_USER_DATA');
                        // Continue to fresh fetch below
                    } else {
                        console.log('App.jsx: Using cached user data from localStorage');
                        console.log('App.jsx: Cached user avatar data:', { 
                            avatar: cachedUserData.avatar, 
                            avatar_id: cachedUserData.avatar_id 
                        });
                        
                        // Process cached data to ensure consistency (add flags if missing)
                        processUserData(cachedUserData);
                        setIsDataFetched(true);
                        return;
                    }
                } catch (parseError) {
                    console.warn('Failed to parse cached user data, fetching fresh data');
                    window.localStorage.removeItem('USER');
                }
            }
            
            // If no token available, don't proceed
            if (!storedToken && !urlToken) {
                setIsDataFetched(true);
                return;
            }
            
            const token = urlToken || storedToken;
            
            // Set token using TokenManager
            TokenManager.setToken(token);
            
            // If token is valid, fetch user data
            const response = await axiosInstance.get(`/auth/me`);
            const userData = response.data;
            
            if (!userData) throw new Error('No user data received');
            
            console.log('App.jsx: Fresh user data from /auth/me');
            console.log('App.jsx: Fresh user avatar data:', { 
                avatar: userData.avatar, 
                avatar_id: userData.avatar_id,
                complete_avatar_relationship: userData.avatar,
                fetch_reason: 'forced_fresh_or_no_cache'
            });
            
            // Process user data and update state
            processUserData(userData);
        } catch (error) {
            console.error('Auth error:', error);
            // Clear storage only if it's an auth error
            if (error.response?.status === 401) {
                window.localStorage.clear();
            };
            setUser(null);
        } finally {
            setIsDataFetched(true);
        }
    }, []);
    
    // Extract user data processing logic
    const processUserData = useCallback((userData) => {
        console.log('processUserData() callback with param userData:', userData);
        console.log('processUserData() avatar debugging:', {
            has_avatar_relationship: !!userData.avatar,
            avatar_id: userData.avatar_id,
            avatar_object: userData.avatar,
            avatar_url: userData.avatar?.url,
            data_source: userData.hasOwnProperty('isKid') ? 'cached_processed' : 'fresh_backend'
        });
        
        // Fix token extraction - could be 'token' or 'access_token'
        const token = userData.token || userData.access_token;
        const { children } = userData;
        
        const isParent = userData.name === "parent" || children?.length > 0 || (userData.roles?.find(role => role.id === 2))?.length > 0;
        const isMentor = (userData.roles?.find(role => role.id === 3))?.length > 0 || userData.name === "mentor" || userData.roles.find(role => role.name === "mentor");
        
        // Check if this data is already processed (has isParent, isMentor, isKid flags)
        const isAlreadyProcessed = userData.hasOwnProperty('isParent') || userData.hasOwnProperty('isMentor') || userData.hasOwnProperty('isKid');
        
        if (isParent && !isMentor) {
            setIsParent(isParent);
            setKids(children?.length > 0 ? children : null);
            
            // Check if we have a previously selected child in local storage
            const selectedChildId = window.localStorage.getItem('SELECTED_CHILD_ID');
            
            // If a child was previously selected, use it
            if (selectedChildId) {
                const selectedChild = children.find(child => child.id === parseInt(selectedChildId));
                if (selectedChild) {
                    // Preserve parentId from existing user data if available (from localStorage)
                    const existingUserData = JSON.parse(window.localStorage.getItem('USER') || '{}');
                    const preservedParentId = existingUserData.parentId || userData.id;
                    
                    // Override with child's properties similar to proceedWithChild
                    const modifiedUserData = { 
                        ...userData, 
                        isParent, 
                        // Override with child's properties
                        id: selectedChild.id,
                        name: selectedChild.name,
                        display_name: selectedChild.display_name,
                        avatar_id: selectedChild.avatar_id,
                        avatar: selectedChild.avatar,
                        gender: selectedChild.gender,
                        nationality: selectedChild.nationality,
                        selectedChild: selectedChild,
                        childId: selectedChild.id,
                        childSettings: selectedChild.settings,
                        // CRITICAL: Preserve original parent ID for API calls
                        parentId: preservedParentId,
                    };
                    setUser(modifiedUserData);
                    console.log("🚀 ~ Called setUser - Line 221 ~ user:", user);
                    setSelectChild(false);
                    
                    // Only store to localStorage if not already processed
                    if (!isAlreadyProcessed) {
                        window.localStorage.setItem("USER", JSON.stringify(modifiedUserData));
                        window.localStorage.setItem("CHILDREN", JSON.stringify(children));
                        window.localStorage.setItem("SETTINGS", JSON.stringify(userData.settings));
                    }
                    return;
                }
            }
            
            // Only show child selection if no child was previously selected
            const parentUserData = { ...userData, access_token: token, isParent };
            setSelectChild(true);
            setUser(parentUserData);
            console.log("🚀 ~ Called setUser - Line 230 ~ user:", user);
            
            // Only store to localStorage if not already processed
            if (!isAlreadyProcessed) {
                window.localStorage.setItem("USER", JSON.stringify(parentUserData));
                window.localStorage.setItem("CHILDREN", JSON.stringify(children));
                window.localStorage.setItem("SETTINGS", JSON.stringify(userData.settings));
            }
        } else if (userData.roles?.find(role => role.id === 3)) {
            setIsMentor(isMentor);
            const mentorUserData = { ...userData, access_token: token, isMentor };
            setUser(mentorUserData);
            console.log("🚀 ~ Called setUser - Line 234 ~ user:", user);
            
            // Only store to localStorage if not already processed
            if (!isAlreadyProcessed) {
                window.localStorage.setItem("USER", JSON.stringify(mentorUserData));
                window.localStorage.setItem("CHILDREN", JSON.stringify(children));
                window.localStorage.setItem("SETTINGS", JSON.stringify(userData.settings));
            }
        } else {
            // Child user - preserve all original data structure including avatar relationship
            const childUserData = { 
                ...userData, 
                access_token: token, 
                isKid: true 
            };
            setUser(childUserData);
            console.log("🚀 ~ Called setUser - Line 237 ~ user:", user);
            console.log("🚀 ~ Child user avatar data:", { 
                avatar: userData.avatar, 
                avatar_id: userData.avatar_id,
                preserved_in_childUserData: childUserData.avatar,
                already_processed: isAlreadyProcessed
            });
            
            // Only store to localStorage if not already processed to avoid overwriting
            if (!isAlreadyProcessed) {
                window.localStorage.setItem("USER", JSON.stringify(childUserData));
                window.localStorage.setItem("CHILDREN", JSON.stringify(children));
                window.localStorage.setItem("SETTINGS", JSON.stringify(userData.settings));
            }
        }
    }, []);
    
    useEffect(() => {
        fetchUser();
    }, [fetchUser]);
    
    return { user, setUser, isParent, isMentor, kids, selectChild, setSelectChild, isDataFetched };
}

// Custom hook for notifications
function useNotification() {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("info");
    
    const showNotification = useCallback((message, severity = "info") => {
        setMessage(message);
        setSeverity(severity);
        setOpen(true);
    }, []);
    
    const hideNotification = useCallback((event, reason) => {
        if (reason === "clickaway") return;
        setOpen(false);
    }, []);
    
    return { open, message, severity, showNotification, hideNotification };
};


export default App;